// @flow
import update from 'immutability-helper';
import { ofType } from 'redux-observable';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { endpoints, fetchCancellationMotivesApi, portCancelPurchasedItem } from './api';
import {
  postSetTrackingNumberApi,
  postConfirmItemReceivedApi,
  postUnconfirmItemReceivedApi,
  postProgressReceive,
} from './api';
import { of } from 'rxjs';
import { setNewAlert } from '../../../alert/duck';

import type { Order } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { Observable } from 'rxjs';

export type TrackingInfo = {
  +trackingCourier: string;
  +trackingNumber: string;
  +trackingUrl?: string;
};

export type CancellationMotive = {
  name: string;
  translations: { [key: string]: string };
};

export type PurchasedAction = {
  +type: string;
  +order?: Order;
  +orderId?: string;
  +itemNumber?: number;
  +trackingInfo?: TrackingInfo;
  +isChecked?: boolean;
  +motives?: CancellationMotive[];
  +error?: HTTPError;
  +motive?: string;
  +message?: ?string;
};

export type PurchasedState = {
  itemReceivedLoadingMap: {
    [key: string]: boolean;
  };
  itemReceivedErrorMap: {
    [key: string]: HTTPError;
  };
  itemTrackingNumberLoadingMap: {
    [key: string]: boolean;
  };
  itemTrackingNumberErrorMap: {
    [key: string]: HTTPError;
  };
  itemLoadingCancelMap: {
    [key: string]: boolean;
  };
  itemCancelErrorMap: {
    [key: string]: HTTPError;
  };
  isProgressing?: boolean;
  progressError?: HTTPError;
  motives?: CancellationMotive[];
  isFetchingMotives?: boolean;
  fetchMotivesError?: boolean;
};


const PURCHASED_POST_SET_TRACKING_NUMBER_REQUEST = 'PURCHASED_POST_SET_TRACKING_NUMBER_REQUEST';
const PURCHASED_POST_SET_TRACKING_NUMBER_SUCCESS = 'PURCHASED_POST_SET_TRACKING_NUMBER_SUCCESS';
const PURCHASED_POST_SET_TRACKING_NUMBER_ERROR = 'PURCHASED_POST_SET_TRACKING_NUMBER_ERROR';

const PURCHASED_POST_CONFIRM_ITEM_RECEIVED_REQUEST = 'PURCHASED_POST_CONFIRM_ITEM_RECEIVED_REQUEST';
const PURCHASED_POST_CONFIRM_ITEM_RECEIVED_SUCCESS = 'PURCHASED_POST_CONFIRM_ITEM_RECEIVED_SUCCESS';
const PURCHASED_POST_CONFIRM_ITEM_RECEIVED_ERROR = 'PURCHASED_POST_CONFIRM_ITEM_RECEIVED_ERROR';

const PURCHASED_POST_PROGRESS_RECEIVE_REQUEST = 'PURCHASED_POST_PROGRESS_RECEIVE_REQUEST';
const PURCHASED_POST_PROGRESS_RECEIVE_SUCCESS = 'PURCHASED_POST_PROGRESS_RECEIVE_SUCCESS';
const PURCHASED_POST_PROGRESS_RECEIVE_ERROR = 'PURCHASED_POST_PROGRESS_RECEIVE_ERROR';

const PURCHASED_FETCH_CANCELATION_MOTIVES_REQUEST = 'PURCHASED_FETCH_CANCELATION_MOTIVES_REQUEST';
const PURCHASED_FETCH_CANCELATION_MOTIVES_SUCCESS = 'PURCHASED_FETCH_CANCELATION_MOTIVES_SUCCESS';
const PURCHASED_FETCH_CANCELATION_MOTIVES_ERROR = 'PURCHASED_FETCH_CANCELATION_MOTIVES_ERROR';

const PURCHASED_CANCEL_ITEM_REQUEST = 'PURCHASED_CANCEL_ITEM_REQUEST';
const PURCHASED_CANCEL_ITEM_SUCCESS = 'PURCHASED_CANCEL_ITEM_SUCCESS';
const PURCHASED_CANCEL_ITEM_ERROR = 'PURCHASED_CANCEL_ITEM_ERROR';

const RESET_STATE = 'RESET_STATE';

// Action Creators
export function setTrackingNumber(orderId: string, itemNumber: number, trackingInfo: TrackingInfo): PurchasedAction {
  return {
    type: PURCHASED_POST_SET_TRACKING_NUMBER_REQUEST,
    itemNumber,
    trackingInfo,
    orderId,
  };
}

export function setTrackingNumberSuccess(itemNumber: number, order: Order): PurchasedAction {
  return {
    type: PURCHASED_POST_SET_TRACKING_NUMBER_SUCCESS,
    itemNumber,
    order,
  };
}

export function setTrackingNumberError(error: HTTPError, itemNumber: number): PurchasedAction {
  return {
    type: PURCHASED_POST_SET_TRACKING_NUMBER_ERROR,
    error,
    itemNumber,
  };
}

export function confirmItemReceived(orderId: string, itemNumber: number, isChecked: boolean): PurchasedAction {
  return {
    type: PURCHASED_POST_CONFIRM_ITEM_RECEIVED_REQUEST,
    orderId,
    itemNumber,
    isChecked,
  };
}

export function confirmItemReceivedSuccess(itemNumber: number, order: Order): PurchasedAction {
  return {
    type: PURCHASED_POST_CONFIRM_ITEM_RECEIVED_SUCCESS,
    itemNumber,
    order,
  };
}

export function confirmItemReceivedError(error: HTTPError, itemNumber: number): PurchasedAction {
  return {
    type: PURCHASED_POST_CONFIRM_ITEM_RECEIVED_ERROR,
    error,
    itemNumber,
  };
}

export function progressReceive(orderId: string): PurchasedAction {
  return {
    type: PURCHASED_POST_PROGRESS_RECEIVE_REQUEST,
    orderId,
  };
}

export function progressReceiveSuccess(order: Order): PurchasedAction {
  return {
    type: PURCHASED_POST_PROGRESS_RECEIVE_SUCCESS,
    order,
  };
}

export function progressReceiveError(error: HTTPError): PurchasedAction {
  return {
    type: PURCHASED_POST_PROGRESS_RECEIVE_ERROR,
    error,
  }
}

export function fetchCancellationMotives(): PurchasedAction {
  return {
    type: PURCHASED_FETCH_CANCELATION_MOTIVES_REQUEST,
  };
}

export function fetchCancellationMotivesSuccess(motives: CancellationMotive[]): PurchasedAction {
  return {
    type: PURCHASED_FETCH_CANCELATION_MOTIVES_SUCCESS,
    motives,
  };
}

export function fetchCancellationMotivesError(error: HTTPError): PurchasedAction {
  return {
    type: PURCHASED_FETCH_CANCELATION_MOTIVES_ERROR,
    error,
  };
}

export function resetState(): PurchasedAction {
  return {
    type: RESET_STATE,
  };
}

export function cancelItem(orderId: string, itemNumber: number, motive: string, message: ?string): PurchasedAction {
  return {
    type: PURCHASED_CANCEL_ITEM_REQUEST,
    orderId,
    itemNumber,
    motive,
    message,
  };
}

export function cancelItemSuccess(itemNumber: number, order: Order): PurchasedAction {
  return {
    type: PURCHASED_CANCEL_ITEM_SUCCESS,
    order,
    itemNumber,
  };
}

export function cancelItemError(error: HTTPError, itemNumber: number): PurchasedAction {
  return {
    type: PURCHASED_CANCEL_ITEM_ERROR,
    error,
    itemNumber,
  };
}

export const actions = {
  PURCHASED_POST_SET_TRACKING_NUMBER_REQUEST,
  PURCHASED_POST_SET_TRACKING_NUMBER_SUCCESS,
  PURCHASED_POST_SET_TRACKING_NUMBER_ERROR,
  PURCHASED_POST_CONFIRM_ITEM_RECEIVED_REQUEST,
  PURCHASED_POST_CONFIRM_ITEM_RECEIVED_SUCCESS,
  PURCHASED_POST_CONFIRM_ITEM_RECEIVED_ERROR,
  PURCHASED_POST_PROGRESS_RECEIVE_REQUEST,
  PURCHASED_POST_PROGRESS_RECEIVE_SUCCESS,
  PURCHASED_POST_PROGRESS_RECEIVE_ERROR,
  PURCHASED_FETCH_CANCELATION_MOTIVES_REQUEST,
  PURCHASED_FETCH_CANCELATION_MOTIVES_SUCCESS,
  PURCHASED_FETCH_CANCELATION_MOTIVES_ERROR,
  PURCHASED_CANCEL_ITEM_REQUEST,
  PURCHASED_CANCEL_ITEM_SUCCESS,
  PURCHASED_CANCEL_ITEM_ERROR,
  RESET_STATE,
  setTrackingNumber,
  setTrackingNumberSuccess,
  setTrackingNumberError,
  confirmItemReceived,
  confirmItemReceivedSuccess,
  confirmItemReceivedError,
  progressReceive,
  progressReceiveSuccess,
  progressReceiveError,
  fetchCancellationMotives,
  fetchCancellationMotivesSuccess,
  fetchCancellationMotivesError,
  cancelItem,
  cancelItemSuccess,
  cancelItemError,
  resetState,
};

export const initialState: PurchasedState = {
  itemReceivedLoadingMap: {},
  itemReceivedErrorMap: {},
  itemTrackingNumberLoadingMap: {},
  itemTrackingNumberErrorMap: {},
  itemLoadingCancelMap: {},
  itemCancelErrorMap: {},
};

export default function reducer(state: PurchasedState = initialState,
                                action: PurchasedAction): PurchasedState {
  switch (action.type) {
    case PURCHASED_POST_SET_TRACKING_NUMBER_REQUEST:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemTrackingNumberLoadingMap: {
          [`${action.itemNumber}`]: { $set: true },
        },
        itemTrackingNumberErrorMap: {
          $unset: [`${action.itemNumber}`],
        }
      });
    case PURCHASED_POST_SET_TRACKING_NUMBER_SUCCESS:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemTrackingNumberLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemTrackingNumberErrorMap: {
          $unset: [`${action.itemNumber}`],
        }
      });
    case PURCHASED_POST_SET_TRACKING_NUMBER_ERROR:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemTrackingNumberLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemTrackingNumberErrorMap: {
          [`${action.itemNumber}`]: { $set: action.error },
        }
      });
    case PURCHASED_POST_CONFIRM_ITEM_RECEIVED_REQUEST:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemReceivedLoadingMap: {
          [`${action.itemNumber}`]: { $set: true },
        },
        itemReceivedErrorMap: {
          $unset: [`${action.itemNumber}`],
        }
      });
    case PURCHASED_POST_CONFIRM_ITEM_RECEIVED_SUCCESS:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemReceivedLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemReceivedErrorMap: {
          $unset: [`${action.itemNumber}`],
        }
      });
    case PURCHASED_POST_CONFIRM_ITEM_RECEIVED_ERROR:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemReceivedLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemReceivedErrorMap: {
          [`${action.itemNumber}`]: { $set: action.error },
        }
      });
    case PURCHASED_POST_PROGRESS_RECEIVE_REQUEST:
      return update(state, {
        isProgressing: { $set: true },
        $unset: ['progressError'],
      });
    case PURCHASED_POST_PROGRESS_RECEIVE_SUCCESS:
      return update(state, {
        isProgressing: { $set: false },
        $unset: ['progressError'],
      });
    case PURCHASED_POST_PROGRESS_RECEIVE_ERROR:
      return update(state, {
        isProgressing: { $set: false },
        progressError: { $set: action.error },
      });
    case PURCHASED_FETCH_CANCELATION_MOTIVES_REQUEST:
      return update(state, {
        isFetchingMotives: { $set: true },
        $unset: ['fetchMotivesError'],
      });
    case PURCHASED_FETCH_CANCELATION_MOTIVES_SUCCESS:
      return update(state, {
        $unset: ['fetchMotivesError', 'isFetchingMotives'],
        motives: { $set: action.motives },
      });
    case PURCHASED_FETCH_CANCELATION_MOTIVES_ERROR:
      return update(state, {
        fetchMotivesError: { $set: action.error },
        $unset: ['isFetchingMotives'],
      });
    case PURCHASED_CANCEL_ITEM_REQUEST:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingCancelMap: {
          [`${action.itemNumber}`]: { $set: true },
        },
        itemCancelErrorMap: {
          $unset: [`${action.itemNumber}`],
        }
      });
    case PURCHASED_CANCEL_ITEM_SUCCESS:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingCancelMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemCancelErrorMap: {
          $unset: [`${action.itemNumber}`],
        }
      });
    case PURCHASED_CANCEL_ITEM_ERROR:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingCancelMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemCancelErrorMap: {
          [`${action.itemNumber}`]: { $set: action.error },
        }
      });
    case RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

export function setTrackingNumberEpic(action$: Observable<PurchasedAction>): Observable<PurchasedAction> {
  return action$.pipe(
    ofType(PURCHASED_POST_SET_TRACKING_NUMBER_REQUEST),
    mergeMap(({ orderId, itemNumber, trackingInfo }) =>
      postSetTrackingNumberApi({ ...trackingInfo }, {
        route: endpoints.SET_TRACKING_NUMBER
          .replace(':orderId', orderId)
          .replace(':itemNumber', itemNumber),
      }).pipe(
        map((order: Order) => setTrackingNumberSuccess(itemNumber, order)),
        catchError((e: HTTPError) => of(setTrackingNumberError(e, itemNumber))))
    ));
}

export function confirmItemReceivedEpic(action$: Observable<PurchasedAction>): Observable<PurchasedAction> {
  return action$.pipe(
    ofType(PURCHASED_POST_CONFIRM_ITEM_RECEIVED_REQUEST),
    mergeMap(({ orderId, itemNumber, isChecked }) => {
      if (isChecked) {
        return postConfirmItemReceivedApi(undefined, {
          route: endpoints.CONFIRM_ITEM_RECEIVED
            .replace(':orderId', orderId)
            .replace(':itemNumber', itemNumber),
        }).pipe(
          map((order: Order) => confirmItemReceivedSuccess(itemNumber, order)),
          catchError((e: HTTPError) => of(confirmItemReceivedError(e, itemNumber))));

      } else {
        return postUnconfirmItemReceivedApi(undefined, {
          route: endpoints.UNCONFIRM_ITEM_RECEIVED
            .replace(':orderId', orderId)
            .replace(':itemNumber', itemNumber),
        }).pipe(
          map((order: Order) => confirmItemReceivedSuccess(itemNumber, order)),
          catchError((e: HTTPError) => of(confirmItemReceivedError(e, itemNumber))));
      }
    })
  );
}

export function progressReceiveEpic(action$: Observable<PurchasedAction>): Observable<PurchasedAction> {
  return action$.pipe(
    ofType(PURCHASED_POST_PROGRESS_RECEIVE_REQUEST),
    mergeMap(({ orderId }) => postProgressReceive(undefined, {
        route: endpoints.PROGRESS_RECEIVE.replace(':orderId', orderId),
      }).pipe(
      map((order: Order) => progressReceiveSuccess(order)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(progressReceiveError(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
          );
        }
        return of(progressReceiveError(e))
      }))
    ));
}

export function fetchCancellationMotivesEpic(action$: Observable<PurchasedAction>): Observable<PurchasedAction> {
  return action$.pipe(
    ofType(PURCHASED_FETCH_CANCELATION_MOTIVES_REQUEST),
    mergeMap(() => fetchCancellationMotivesApi()
      .pipe(
        map((motives: CancellationMotive[]) => fetchCancellationMotivesSuccess(motives)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(progressReceiveError(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
            );
          }
          return of(fetchCancellationMotivesError(e))
        }))
    ));
}

export function cancelPurchasedItemEpic(action$: Observable<PurchasedAction>): Observable<PurchasedAction> {
  return action$.pipe(
    ofType(PURCHASED_CANCEL_ITEM_REQUEST),
    mergeMap(({ orderId, itemNumber, motive, message }) => {
        const req: { [key: string]: string } = {
          cancellationMotive: motive,
        };
        if (message) {
          req.cancellationMessage = message;
        }
        return portCancelPurchasedItem(req, {
          route: endpoints.CANCEL_PURCHASED_ITEM
            .replace(':orderId', orderId)
            .replace(':itemNumber', itemNumber),
        }).pipe(
          map((order: Order) => cancelItemSuccess(itemNumber, order)),
          catchError((e: HTTPError) => {
            if (Math.round(e.code / 100) === 5) {
              return of(cancelItemError(e, itemNumber),
                setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
              );
            }
            return of(cancelItemError(e, itemNumber));
          }))
      }
    ));
}



// WEBPACK FOOTER //
// ./src/modules/order/Sale/purchased/duck.js